import { Component, computed, inject, input, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { FieldsetModule } from 'primeng/fieldset';
import { InputTextModule } from 'primeng/inputtext';
import { RadioButtonModule } from 'primeng/radiobutton';
import { TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService, MessageService } from 'primeng/api';
import { LoadingOverlayComponent } from '../shared/loading-overlay.component';
import {
  BusquedaAsesorDialogComponent,
  AsesorSeleccionado
} from '../common/busquedas/busqueda-asesor-dialog.component';
import {
  BusquedaClienteDialogComponent,
  ClienteSeleccionado
} from '../common/busquedas/busqueda-cliente-dialog.component';
import { DEFAULT_ROWS_PER_PAGE, PAGE_SIZE_OPTIONS, YES_VALUE, NO_VALUE } from 'src/app/common/constants-ejb';
import { sanitizeText } from 'src/app/common/input-utils';
import {
  CumplimientoRectificacion,
  RectificarTransporteCumplimientoPageResponse,
  RectificarTransporteService
} from 'src/app/domain/service/servicios-entregados/rectificar-transporte.service';
import { FacturaEncabezadoRepository } from 'src/app/domain/service/servicios-entregados/factura-encabezado-repository';

/**
 * Componente Dialogo: Rectificar Transporte en Prefactura.
 * Legacy: RectificarTransporteViewImpl.java + RectificarTransporteActivity.java
 *
 * Se abre desde Detalle Servicios Entregados.
 * Permite listar cumplimientos de una factura filtrados por pago transporte (Si/No)
 * y anular o asignar el pago de transporte a los seleccionados.
 */
@Component({
  selector: 'app-rectificar-transporte',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    DatePickerModule,
    DialogModule,
    DropdownModule,
    FieldsetModule,
    InputTextModule,
    RadioButtonModule,
    TableModule,
    ToastModule,
    ConfirmDialogModule,
    LoadingOverlayComponent,
    BusquedaAsesorDialogComponent,
    BusquedaClienteDialogComponent
  ],
  styleUrls: ['../shared/styles/grid-table.styles.scss'],
  providers: [MessageService, ConfirmationService],
  template: `
    <p-toast></p-toast>
    <p-confirmDialog></p-confirmDialog>
    <p-dialog
      header="Rectificar Transporte"
      [(visible)]="dialogVisible"
      [modal]="true"
      [style]="{ width: '90vw', maxWidth: '1400px' }"
      [maximizable]="true"
      [closable]="false"
      [closeOnEscape]="false"
      [dismissableMask]="false"
      [contentStyle]="{ overflow: 'auto', maxHeight: '80vh' }">

      <app-loading-overlay [blocked]="loading()"></app-loading-overlay>

      <!-- Barra de acciones (legacy: Anular | Asignar | Recalcular prefactura | Regresar) -->
      <div class="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center justify-end gap-2 sm:gap-3 mb-4 pb-2 border-b border-gray-200 dark:border-gray-700">
        <p-button label="Regresar" icon="pi pi-arrow-left"
          [outlined]="true" severity="secondary" size="small"
          styleClass="w-full sm:w-auto min-h-[44px]"
          (onClick)="cerrar()"></p-button>
        <p-button label="Recalcular Prefactura" icon="pi pi-refresh"
          [outlined]="true" severity="secondary" size="small"
          styleClass="w-full sm:w-auto min-h-[44px]"
          (onClick)="recalcularPrefactura()"></p-button>
        <p-button label="Asignar" icon="pi pi-check"
          severity="success" size="small"
          styleClass="w-full sm:w-auto min-h-[44px]"
          [disabled]="selectedCumplimientos.length === 0 || filtroPagoTransporte !== NO_VALUE"
          (onClick)="confirmarAsignar()"></p-button>
        <p-button label="Anular" icon="pi pi-times"
          severity="danger" size="small"
          styleClass="w-full sm:w-auto min-h-[44px]"
          [disabled]="selectedCumplimientos.length === 0 || filtroPagoTransporte !== YES_VALUE"
          (onClick)="confirmarAnular()"></p-button>
      </div>

      <!-- Filtros en fieldset -->
      <p-fieldset legend="Rectificar Transporte" [toggleable]="true" styleClass="mb-4">
        <div class="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Codigo Cumplimiento</label>
            <input pInputText type="text" [(ngModel)]="filtroCumplimiento"
              (input)="filtroCumplimiento = sanitizeText(filtroCumplimiento)"
              maxlength="20" class="w-full" />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Numero Orientacion</label>
            <input pInputText type="text" [(ngModel)]="filtroOrden"
              (input)="filtroOrden = sanitizeText(filtroOrden)"
              maxlength="20" class="w-full" />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Fecha Prestacion Servicio</label>
            <p-datepicker [(ngModel)]="filtroFechaPrestacion" dateFormat="yy/mm/dd" [showIcon]="true" styleClass="w-full"></p-datepicker>
          </div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Pago Transporte <span class="text-red-500">*</span></label>
            <div class="flex gap-4 items-center mt-1">
              <div class="flex items-center gap-2">
                <p-radioButton name="pagoTransporte" [value]="YES_VALUE" [(ngModel)]="filtroPagoTransporte" inputId="pagoSi"></p-radioButton>
                <label for="pagoSi" class="cursor-pointer text-gray-700 dark:text-gray-300">Sí</label>
              </div>
              <div class="flex items-center gap-2">
                <p-radioButton name="pagoTransporte" [value]="NO_VALUE" [(ngModel)]="filtroPagoTransporte" inputId="pagoNo"></p-radioButton>
                <label for="pagoNo" class="cursor-pointer text-gray-700 dark:text-gray-300">No</label>
              </div>
            </div>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Cliente</label>
            <div class="flex gap-2">
              <input pInputText [value]="clienteSeleccionadoLabel()" readonly placeholder="Seleccione cliente" class="w-full cursor-pointer" (click)="mostrarBuscadorCliente = true" />
              <button pButton type="button" icon="pi pi-search"
                class="p-button-outlined p-button-sm min-w-[44px] min-h-[44px]"
                pTooltip="Buscar cliente"
                (click)="mostrarBuscadorCliente = true"></button>
              @if (filtroNmconsecutivoBeneficiario) {
                <button pButton type="button" icon="pi pi-times"
                  class="p-button-text p-button-danger min-w-[44px] min-h-[44px]"
                  pTooltip="Limpiar cliente"
                  (click)="limpiarCliente()"></button>
              }
            </div>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Consultor</label>
            <div class="flex gap-2">
              <input pInputText [value]="asesorSeleccionadoLabel()" readonly placeholder="Seleccione consultor" class="w-full cursor-pointer" (click)="mostrarBuscadorAsesor = true" />
              <button pButton type="button" icon="pi pi-search"
                class="p-button-outlined p-button-sm min-w-[44px] min-h-[44px]"
                pTooltip="Buscar consultor"
                (click)="mostrarBuscadorAsesor = true"></button>
              @if (filtroNmconsecutivoAsesor) {
                <button pButton type="button" icon="pi pi-times"
                  class="p-button-text p-button-danger min-w-[44px] min-h-[44px]"
                  pTooltip="Limpiar consultor"
                  (click)="limpiarAsesor()"></button>
              }
            </div>
          </div>
        </div>

        <div class="flex flex-wrap justify-end gap-2">
          <p-button label="Limpiar" icon="pi pi-eraser"
            severity="secondary" [outlined]="true"
            styleClass="min-h-[44px]"
            (onClick)="limpiar()"></p-button>
          <p-button label="Buscar" icon="pi pi-search"
            styleClass="min-h-[44px]"
            [disabled]="!filtroPagoTransporte"
            (onClick)="buscar()"></p-button>
        </div>
      </p-fieldset>

      <!-- Buscadores -->
      @if (mostrarBuscadorAsesor) {
        <app-asesor-search
          (onSelect)="onAsesorSeleccionado($event)"
          (onClose)="mostrarBuscadorAsesor = false">
        </app-asesor-search>
      }
      @if (mostrarBuscadorCliente) {
        <app-busqueda-cliente-dialog
          [titulo]="'Buscar Cliente'"
          (onSelect)="onClienteSeleccionado($event)"
          (onClose)="mostrarBuscadorCliente = false">
        </app-busqueda-cliente-dialog>
      }

      <!-- Titulo tabla + Registros -->
      <div class="flex justify-between items-center px-3 pb-2">
        <h3 class="text-lg font-semibold text-gray-700 dark:text-gray-200 m-0">Listado de Cumplimientos</h3>
        @if (totalRegistros() > 0) {
          <span class="text-sm text-gray-500 dark:text-gray-400">Registros del {{ registroDesde() }} hasta el {{ registroHasta() }} de {{ totalRegistros() }}</span>
        }
      </div>

      <!-- Tabla resultados -->
      <div class="md:hidden space-y-3">
        @if (!loading() && paginatedData().length === 0) {
          <div class="text-center py-4 text-gray-500 border border-gray-200 dark:border-gray-700 rounded-lg">
            No se encontraron cumplimientos.
          </div>
        }

        @for (item of paginatedData(); track item.nmconsecutivoCumplimiento ?? $index) {
          <div class="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-white dark:bg-gray-900">
            <div class="flex items-start justify-between gap-2 mb-2">
              <div class="text-sm font-semibold text-gray-800 dark:text-gray-100">
                Cumplimiento {{ item.nmconsecutivoCumplimiento }}
              </div>
              <input
                type="checkbox"
                class="h-5 w-5 mt-1"
                [checked]="estaSeleccionado(item)"
                (change)="toggleSeleccion(item, $any($event.target).checked)" />
            </div>

            <div class="grid grid-cols-1 gap-2 text-sm">
              <div><span class="text-gray-500">Cliente:</span> <span class="text-gray-800 dark:text-gray-200">{{ item.dscliente || '-' }}</span></div>
              <div><span class="text-gray-500">Consultor:</span> <span class="text-gray-800 dark:text-gray-200">{{ item.dsasesor || '-' }}</span></div>
              <div><span class="text-gray-500">Fecha prestación:</span> <span class="text-gray-800 dark:text-gray-200">{{ item.feprogramacion | date:'yyyy-MM-dd HH:mm' }}</span></div>
              <div><span class="text-gray-500">Pago transporte:</span> <span class="text-gray-800 dark:text-gray-200">{{ item.snpagoTransporteEmpleado === 'S' ? 'Si' : 'No' }}</span></div>
            </div>
          </div>
        }
      </div>

      <div class="hidden md:block">
      <p-table
        [resizableColumns]="true"
        columnResizeMode="expand"
        [value]="paginatedData()"
        [paginator]="false"
        [(selection)]="selectedCumplimientos"
        dataKey="nmconsecutivoCumplimiento"
        [scrollable]="true"
        [responsiveLayout]="'scroll'"
        [tableStyle]="{ 'min-width': '72rem' }"
        styleClass="tabla-rectificar-transporte p-datatable-sm p-datatable-gridlines">
        <ng-template pTemplate="header">
          <tr>
            <th pResizableColumn style="width: 3rem">
              <p-tableHeaderCheckbox></p-tableHeaderCheckbox>
            </th>
            <th pResizableColumn>Nro. Cumplimiento</th>
            <th pResizableColumn>Cliente</th>
            <th pResizableColumn>Consultor</th>
            <th pResizableColumn>Fecha Prestacion Servicio</th>
            <th pResizableColumn>Pago Transporte</th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-item>
          <tr>
            <td>
              <p-tableCheckbox [value]="item"></p-tableCheckbox>
            </td>
            <td>{{ item.nmconsecutivoCumplimiento }}</td>
            <td>{{ item.dscliente }}</td>
            <td>{{ item.dsasesor }}</td>
            <td>{{ item.feprogramacion | date:'yyyy-MM-dd HH:mm' }}</td>
            <td>{{ item.snpagoTransporteEmpleado === YES_VALUE ? 'Si' : 'No' }}</td>
          </tr>
        </ng-template>
        <ng-template pTemplate="emptymessage">
          <tr>
            <td colspan="6" class="text-center py-4 text-gray-500 dark:text-gray-400">No se encontraron cumplimientos.</td>
          </tr>
        </ng-template>
      </p-table>
      </div>

      <!-- Paginacion manual -->
      @if (totalRegistros() > 0) {
        <div class="flex justify-between items-center p-3 border-t dark:border-gray-700 mt-2">
          <div class="text-gray-600 dark:text-gray-400 text-sm">
            Página {{ currentPage() }} de {{ totalPages() }}
          </div>
          <div class="flex gap-2 items-center flex-wrap">
            <label class="text-sm text-gray-600 dark:text-gray-400">Registros:</label>
            <p-dropdown
              [options]="pageSizeOptions"
              [(ngModel)]="selectedPageSize"
              (onChange)="onPageSizeChange()"
              [style]="{ width: '90px' }"
              appendTo="body"
              scrollHeight="150px" />
            <p-button styleClass="min-w-[44px] min-h-[44px]" icon="pi pi-angle-double-left"
              severity="secondary" size="small"
              [disabled]="!hasPrevious()"
              (onClick)="irPrimeraPagina()" />
            <p-button styleClass="min-w-[44px] min-h-[44px]" icon="pi pi-angle-left"
              severity="secondary" size="small"
              [disabled]="!hasPrevious()"
              (onClick)="irPaginaAnterior()" />
            <p-button styleClass="min-w-[44px] min-h-[44px]" icon="pi pi-angle-right"
              severity="secondary" size="small"
              [disabled]="!hasNext()"
              (onClick)="irPaginaSiguiente()" />
            <p-button styleClass="min-w-[44px] min-h-[44px]" icon="pi pi-angle-double-right"
              severity="secondary" size="small"
              [disabled]="!hasNext()"
              (onClick)="irUltimaPagina()" />
          </div>
        </div>
      }
    </p-dialog>
  `
})
export class RectificarTransporteComponent {
  private readonly rectificarService = inject(RectificarTransporteService);
  private readonly facturaRepository = inject(FacturaEncabezadoRepository);
  private readonly messageService = inject(MessageService);
  private readonly confirmationService = inject(ConfirmationService);

  readonly YES_VALUE = YES_VALUE;
  readonly NO_VALUE = NO_VALUE;
  readonly sanitizeText = sanitizeText;

  // Inputs/Outputs
  nmconsecutivoFactura = input.required<number>();
  cerrarDialog = output<void>();
  recalcular = output<void>();

  // Dialog state
  dialogVisible = true;

  // Filtros
  filtroCumplimiento: string | null = null;
  filtroOrden = '';
  filtroFechaPrestacion: Date | null = null;
  filtroPagoTransporte = '';
  filtroNmconsecutivoBeneficiario: number | null = null;
  filtroNmconsecutivoAsesor: number | null = null;

  // Buscadores
  mostrarBuscadorCliente = false;
  mostrarBuscadorAsesor = false;

  // Estado
  loading = signal(false);
  pageResponse = signal<RectificarTransporteCumplimientoPageResponse>({
    content: [], totalElements: 0, nextCursor: null, previousCursor: null, hasNext: false, hasPrevious: false
  });
  selectedCumplimientos: CumplimientoRectificacion[] = [];
  // Legacy: recalculoDone — indica si se recalculó la prefactura después de rectificar
  recalculoDone = false;
  operacionRealizada = false;

  // Paginacion SERVER-SIDE (Keyset/Cursor). currentPage es solo informativo
  // (contador de páginas visitadas en la sesión de navegación actual): no
  // permite saltar a una página arbitraria, sólo primera/anterior/siguiente/última.
  currentPage = signal(1);
  pageSize = signal(DEFAULT_ROWS_PER_PAGE);
  pageSizeOptions = PAGE_SIZE_OPTIONS.map(v => ({ label: String(v), value: v }));
  selectedPageSize = DEFAULT_ROWS_PER_PAGE;

  // Computed desde la respuesta del servidor
  paginatedData = computed(() => this.pageResponse().content);
  totalRegistros = computed(() => this.pageResponse().totalElements);
  totalPages = computed(() => this.totalRegistros() > 0 ? Math.ceil(this.totalRegistros() / this.pageSize()) : 0);
  hasPrevious = computed(() => this.pageResponse().hasPrevious);
  hasNext = computed(() => this.pageResponse().hasNext);
  registroDesde = computed(() => this.totalRegistros() === 0 ? 0 : (this.currentPage() - 1) * this.pageSize() + 1);
  registroHasta = computed(() => Math.min(this.registroDesde() + this.pageResponse().content.length - 1, this.totalRegistros()));

  clienteSeleccionadoLabel = signal('');
  asesorSeleccionadoLabel = signal('');

  cerrar(): void {
    // Legacy: si se realizó operación pero no se recalculó, confirmar antes de salir
    if (this.operacionRealizada && !this.recalculoDone) {
      this.confirmationService.confirm({
        message: 'Se realizaron cambios y no se ha recalculado la prefactura. ¿Desea salir de todos modos?',
        header: 'Confirmar Salida',
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
          this.dialogVisible = false;
          this.cerrarDialog.emit();
        }
      });
    } else {
      this.dialogVisible = false;
      this.cerrarDialog.emit();
    }
  }

  recalcularPrefactura(): void {
    this.loading.set(true);
    this.facturaRepository.agregarDetalle(this.nmconsecutivoFactura()).subscribe({
      next: (res) => {
        this.loading.set(false);
        if (res.exito) {
          this.recalculoDone = true;
          this.messageService.add({ severity: 'success', summary: 'Éxito', detail: 'Se recalculó la prefactura exitosamente' });
          this.recalcular.emit();
        } else {
          this.recalculoDone = false;
          this.messageService.add({ severity: 'error', summary: 'Error', detail: res.mensaje || 'Error al recalcular la prefactura' });
        }
      },
      error: (err) => {
        this.loading.set(false);
        this.recalculoDone = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.error?.message || 'Error al recalcular la prefactura' });
      }
    });
  }

  onClienteSeleccionado(cliente: ClienteSeleccionado): void {
    this.filtroNmconsecutivoBeneficiario = cliente.nmconsecutivo;
    this.clienteSeleccionadoLabel.set(cliente.nombreComercial || '');
    this.mostrarBuscadorCliente = false;
  }

  limpiarCliente(): void {
    this.filtroNmconsecutivoBeneficiario = null;
    this.clienteSeleccionadoLabel.set('');
  }

  onAsesorSeleccionado(asesor: AsesorSeleccionado): void {
    this.filtroNmconsecutivoAsesor = asesor.nmconsecutivo || asesor.cdpersona;
    this.asesorSeleccionadoLabel.set(asesor.dsnombreComercial || '');
    this.mostrarBuscadorAsesor = false;
  }

  limpiarAsesor(): void {
    this.filtroNmconsecutivoAsesor = null;
    this.asesorSeleccionadoLabel.set('');
  }

  buscar(): void {
    this.currentPage.set(1);
    this.ejecutarBusqueda(undefined, undefined);
  }

  private ejecutarBusqueda(cursor: string | undefined, direction: 'NEXT' | 'PREVIOUS' | undefined): void {
    if (!this.filtroPagoTransporte) {
      this.messageService.add({ severity: 'warn', summary: 'Validacion', detail: 'El campo Pago Transporte es obligatorio' });
      return;
    }

    this.loading.set(true);
    this.selectedCumplimientos = [];

    const filtro = {
      snpagoTransporteEmpleado: this.filtroPagoTransporte,
      nmconsecutivoCumplimiento: this.filtroCumplimiento ? +this.filtroCumplimiento : undefined,
      dsorden: this.filtroOrden || undefined,
      nmconsecutivoBeneficiario: this.filtroNmconsecutivoBeneficiario || undefined,
      nmconsecutivoAsesor: this.filtroNmconsecutivoAsesor || undefined,
      feprogramacion: this.filtroFechaPrestacion
        ? this.filtroFechaPrestacion.toISOString().split('T')[0]
        : undefined,
      cursor,
      size: this.pageSize(),
      direction
    };

    this.rectificarService.listarCumplimientos(this.nmconsecutivoFactura(), filtro).subscribe({
      next: (res) => {
        this.pageResponse.set(res);
        this.loading.set(false);
      },
      error: (err) => {
        this.loading.set(false);
        this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.error?.message || 'Error al consultar cumplimientos' });
      }
    });
  }

  limpiar(): void {
    this.filtroCumplimiento = null;
    this.filtroOrden = '';
    this.filtroFechaPrestacion = null;
    this.filtroPagoTransporte = '';
    this.filtroNmconsecutivoBeneficiario = null;
    this.filtroNmconsecutivoAsesor = null;
    this.clienteSeleccionadoLabel.set('');
    this.asesorSeleccionadoLabel.set('');
    this.pageResponse.set({
      content: [], totalElements: 0, nextCursor: null, previousCursor: null, hasNext: false, hasPrevious: false
    });
    this.selectedCumplimientos = [];
    this.currentPage.set(1);
  }

  confirmarAnular(): void {
    this.confirmationService.confirm({
      message: `Esta seguro de anular el transporte para ${this.selectedCumplimientos.length} cumplimiento(s)?`,
      header: 'Confirmar Anulacion',
      icon: 'pi pi-exclamation-triangle',
      accept: () => this.ejecutarAnular()
    });
  }

  confirmarAsignar(): void {
    this.confirmationService.confirm({
      message: `Esta seguro de asignar transporte para ${this.selectedCumplimientos.length} cumplimiento(s)?`,
      header: 'Confirmar Asignacion',
      icon: 'pi pi-exclamation-triangle',
      accept: () => this.ejecutarAsignar()
    });
  }

  private ejecutarAnular(): void {
    this.loading.set(true);
    const ids = this.selectedCumplimientos.map(c => c.nmconsecutivoCumplimiento);

    this.rectificarService.anularTransporte(this.nmconsecutivoFactura(), ids).subscribe({
      next: (res) => {
        this.loading.set(false);
        if (res.exito) {
          this.operacionRealizada = true;
          this.recalculoDone = false;
          this.messageService.add({
            severity: 'success', summary: 'Exito',
            detail: 'Se anularon correctamente los transportes de los cumplimientos seleccionados. Recuerde recalcular la prefactura'
          });
          this.buscar();
        } else {
          this.messageService.add({ severity: 'error', summary: 'Error', detail: res.mensaje });
        }
      },
      error: (err) => {
        this.loading.set(false);
        this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.error?.message || 'Error al anular transporte' });
      }
    });
  }

  private ejecutarAsignar(): void {
    this.loading.set(true);

    this.rectificarService.asignarTransporte(this.nmconsecutivoFactura(), this.selectedCumplimientos).subscribe({
      next: (res) => {
        this.loading.set(false);
        if (res.exito) {
          this.operacionRealizada = true;
          this.recalculoDone = false;
          this.messageService.add({
            severity: 'success', summary: 'Exito',
            detail: 'Se asignaron correctamente los transportes de los cumplimientos seleccionados. Recuerde recalcular la prefactura'
          });
          this.buscar();
        } else {
          this.messageService.add({ severity: 'error', summary: 'Error', detail: res.mensaje });
        }
      },
      error: (err) => {
        this.loading.set(false);
        this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.error?.message || 'Error al asignar transporte' });
      }
    });
  }

  // Paginacion Keyset/Cursor — cada cambio de página llama al servidor con el
  // cursor correspondiente. No es posible saltar a una página arbitraria (N);
  // sólo primera (reinicia cursor), anterior, siguiente y última
  // (PREVIOUS sin cursor => última página, soportado por el backend para este
  // endpoint específicamente — ver contrato-paginacion-keyset-lote1.md).
  irPrimeraPagina(): void {
    this.currentPage.set(1);
    this.ejecutarBusqueda(undefined, undefined);
  }
  irUltimaPagina(): void {
    this.currentPage.set(this.totalPages() || 1);
    this.ejecutarBusqueda(undefined, 'PREVIOUS');
  }
  irPaginaAnterior(): void {
    if (this.hasPrevious()) {
      this.currentPage.update(p => Math.max(1, p - 1));
      this.ejecutarBusqueda(this.pageResponse().previousCursor ?? undefined, 'PREVIOUS');
    }
  }
  irPaginaSiguiente(): void {
    if (this.hasNext()) {
      this.currentPage.update(p => p + 1);
      this.ejecutarBusqueda(this.pageResponse().nextCursor ?? undefined, 'NEXT');
    }
  }
  onPageSizeChange(): void {
    this.pageSize.set(this.selectedPageSize);
    this.currentPage.set(1);
    this.ejecutarBusqueda(undefined, undefined);
  }

  estaSeleccionado(item: CumplimientoRectificacion): boolean {
    return this.selectedCumplimientos.some(
      (sel) => sel.nmconsecutivoCumplimiento === item.nmconsecutivoCumplimiento
    );
  }

  toggleSeleccion(item: CumplimientoRectificacion, checked: boolean): void {
    if (checked) {
      if (!this.estaSeleccionado(item)) {
        this.selectedCumplimientos = [...this.selectedCumplimientos, item];
      }
      return;
    }

    this.selectedCumplimientos = this.selectedCumplimientos.filter(
      (sel) => sel.nmconsecutivoCumplimiento !== item.nmconsecutivoCumplimiento
    );
  }
}
